import React, { useEffect } from 'react';
import { Form, Switch, Typography, Alert } from 'antd';
import type { FormProps } from 'antd';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import { taxCodeValidationRule } from '@nxsys/utils/formFieldValidation';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import {
  defaultPaymentFrequency,
  umbrellaHolidayPaymentMethodOptions,
} from '@nxsys/constants/AppConstants';
import AppCard from '@nxsys/components/AppCard';
import { UserSearchIcon } from '@nxsys/components/icons';
import { find } from 'lodash';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { calcKUmbrellaValue } from '@nxsys/store/slices/kid/actions';
import { AppDispatch, RootState } from '@nxsys/store';
import { kidAction } from '@nxsys/store/slices/kid/kidSlice';
import {
  KidFormData,
  KidParams,
  OptionType,
  CandidateInfo,
  CompanyPaymentInfo
} from '@nxsys/store/slices/kid/types';
import { showToast } from '@nxsys/utils/toast';
import AppButton from '@nxsys/components/AppButton';
import { showCurrencyByCountry, getAge } from '@nxsys/utils';
import AppRadioInput from '@nxsys/components/common/AppRadioInput';
import { AppRadioGroup } from '@nxsys/components/common/AppRadioInput';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import AppRateInput from '@nxsys/components/AppRateInput';

const taxYearOptions = [
  { label: '2026', value: 2026 },
  { label: '2025', value: 2025 },
];

// Utility function to safely convert string or number to number
const toNumber = (value: string | number | undefined | null): number => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  if (typeof value === 'number') {
    return isNaN(value) ? 0 : value;
  }

  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  }

  return 0;
};

// AppSelect is labelInValue, so once a year is picked the form holds
// { label, value } while the initial value is still a plain number.
const toTaxYear = (value: KidFormData['taxYear']): number | undefined =>
  toNumber(
    typeof value === 'object' && value !== null ? value.value : value,
  ) || undefined;

const CreateForm: React.FC = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = React.useState(false);
  const { isAgency, user, payrollCountry } = useSelector(
    (state: RootState) => state.appStore,
  );
  const agencyPortalId = isAgency ? user?.agencyId?.id : null;
  const agencyCompanyRef = isAgency ? user?.agencyContactRef?.companyRef : null;

  const handleCalculate = async () => {
    const values = form.getFieldsValue(true);

    const {
      isCandidateExist,
      agencyId = null,
      payFrequency = null,
      noOfHours = null,
      candidateId = null,
      // emailId = null,
      payRatePerHour = null,
      companyId = null,
      candidateName,
      taxCode,
      age,
      appLevy,
      pension,
      expenses,
      holidayPay,
      margin,
      taxYear,
    } = values;

    const params = {
      isCandidateExist,
      companyId: companyId?.value || null,
      candidateId: isCandidateExist ? candidateId?.value : null,
      candidateName: !isCandidateExist ? candidateName : null,
      agencyId: agencyId?.value || agencyPortalId || null,
      // emailId,
      payFrequency: payFrequency?.label || null,
      noOfHours: toNumber(noOfHours),
      payRatePerHour: toNumber(payRatePerHour),
      taxCode: taxCode || '',
      age: toNumber(age) || null,
      appLevy: appLevy || false,
      pension: pension || false,
      expenses: expenses ? toNumber(expenses) : undefined,
      holidayPay: holidayPay?.value || null,
      margin: toNumber(margin) || undefined,
      taxYear: toTaxYear(taxYear),
    };
    try {
      setLoading(true);
      const calculate = await dispatch(calcKUmbrellaValue(params)).unwrap();
      if (!!calculate?.errors?.length) {
        showToast({ type: 'error', message: calculate?.errors?.join?.('\n') });
      }
    } catch (error: any) {
      console.log('error', error);
      const errorMessage = error?.message || 'Something went wrong';
      showToast({ type: 'error', message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const convertFormDataToParams = (values: KidFormData): KidParams => {
    const {
      isCandidateExist,
      agencyId = null,
      payFrequency = null,
      noOfHours = null,
      candidateId = null,
      emailId = null,
      payRatePerHour = null,
      companyId = null,
      candidateName,
      taxCode,
      age,
      appLevy,
      pension,
      expenses,
      holidayPay,
      margin,
      taxYear,
    } = values;

    return {
      isCandidateExist,
      companyId: companyId?.value || null,
      candidateId: isCandidateExist ? candidateId?.value : null,
      candidateName: !isCandidateExist ? candidateName : candidateId?.label,
      agencyId: agencyId?.value || agencyPortalId  || null,
      emailId: emailId || '',
      payFrequency: payFrequency?.label || null,
      noOfHours: toNumber(noOfHours),
      payRatePerHour: toNumber(payRatePerHour),
      taxCode: taxCode || '',
      age: toNumber(age) || null,
      expenses: expenses ? toNumber(expenses) : null,
      holidayPay: (holidayPay?.value as string) || null,
      margin: toNumber(margin) || null,
      appLevy: appLevy || false,
      pension: pension || false,
      taxYear: toTaxYear(taxYear),
    };
  };

  const handlePreviewAction = () => {
    const values = form.getFieldsValue(true);

    const params = convertFormDataToParams(values);
    dispatch(kidAction.setPreviewData({ formData: params }));
    handleCalculate();
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  const onValuesChange = (changedValues: Partial<KidFormData>) => {
    if ('isCandidateExist' in changedValues) {
      form.resetFields();
      form.setFieldsValue({
        isCandidateExist: changedValues.isCandidateExist,
        ...(agencyCompanyRef && {
          companyId: {
            value: agencyCompanyRef.id,
            label: agencyCompanyRef.companyName,
          },
        }),
      });
    }
    const values = form.getFieldsValue(true);
    const params = convertFormDataToParams(values);
    dispatch(kidAction.setPreviewData({ formData: params }));
  };

  const handleChangeCandidateExist = (value: CandidateInfo) => {
    const { DOB, payFrequency, email, setting } = value?.fullData || {};
    const payFrequencyOption =
      find(defaultPaymentFrequency, (item) => item?.value === payFrequency) ||
      null;
    const holidayPayMethodOption =
      find(
        umbrellaHolidayPaymentMethodOptions,
        (item) => item?.value === setting?.holidayPayMethod,
      ) || null;

    // Use candidate/subcontractor defaults
    const currentValues = form.getFieldsValue(true);
    form.setFieldsValue({
      candidateId: value,
      age: DOB ? getAge(new Date(DOB)): currentValues?.age,
      emailId: email || currentValues?.email,
      taxCode: setting?.taxCode || currentValues?.taxCode,
      payFrequency: payFrequencyOption || currentValues?.payFrequency,
      holidayPay: holidayPayMethodOption || currentValues?.holidayPay,
      margin: setting?.margin ? Number(setting?.margin): currentValues?.margin,
    });
    onValuesChange({});
  };

  const handleChangeCompany = (value: CompanyPaymentInfo) => {
    const { paymentFrequency, holidayPayMethod, taxCode, margin } = value?.fullData || {};
    const payFrequencyOption =
      find(
        defaultPaymentFrequency,
        (item) => item?.value === paymentFrequency,
      ) || null;
    // Find holiday pay method option
    const holidayPayMethodOption = find(
      umbrellaHolidayPaymentMethodOptions,
      (item) => item?.value === holidayPayMethod,
    );
    const marginCompany = margin ? Number(margin): null;
    // Pre-fill company defaults (only if current fields are empty)
    const currentValues = form.getFieldsValue(true);
    if (currentValues?.candidateId) {
      form.setFieldsValue({
        companyId: value,
        payFrequency: currentValues?.payFrequency ||
          payFrequencyOption || { label: 'Weekly', value: 'WEEKLY' },
        taxCode: currentValues.taxCode || taxCode || '1257L',
        holidayPay: currentValues.holidayPay ||
          holidayPayMethodOption || { label: 'Advanced', value: 'ROLLED_UP' },
        margin: currentValues?.margin || marginCompany || 15,
        age: currentValues?.age || 25,
      });
    } else {
      form.setFieldsValue({
        companyId: value,
        payFrequency: payFrequencyOption || { label: 'Weekly', value: 'WEEKLY' },
        taxCode: taxCode || '1257L',
        holidayPay: holidayPayMethodOption || { label: 'Advanced', value: 'ROLLED_UP' },
        margin: marginCompany || 15,
        age: 25,
      });
    }
    onValuesChange({});
  };

  useEffect(() => {
    if (agencyCompanyRef) {
      handleChangeCompany({
        value: agencyCompanyRef.id,
        label: agencyCompanyRef.companyName,
        fullData: agencyCompanyRef,
      } as CompanyPaymentInfo);
    }
  }, [agencyCompanyRef]);

  return (
    <>
      <div className="flex justify-between items-center px-5 py-3 border-b border-neutral-200">
        <Typography.Title level={4} className="mt-0 mb-0 flex-shrink-0">
          Create KID
        </Typography.Title>
        <AppButton
          key="calculate"
          name="Calculate"
          variant="outlined"
          type="primary"
          disabled={true}
          className="invisible"
        />
      </div>
      <div className="w-full max-w-full p-[20px]">
        <AppFormContainer
          form={form}
          initialValues={{
            isCandidateExist: false,
            companyId: null,
            candidateId: null,
            candidateName: '',
            agencyId: null,
            payFrequency: { label: 'Weekly', value: 'WEEKLY' },
            emailId: '',
            noOfHours: 40,
            margin: 15,
            payRatePerHour: 15,
            taxCode: '1257L',
            age: 25,
            expenses: '',
            holidayPay: { label: 'Advanced', value: 'ROLLED_UP' },
            appLevy: false,
            pension: false,
            taxYear: 2026,
          }}
          onFinish={() => handlePreviewAction()}
          onFinishFailed={onFinishFailed}
          onValuesChange={onValuesChange}
        >
          <AppCard className="p-4 mb-4">
            <div className="flex flex-row items-center">
              <UserSearchIcon />
              <div className="ml-4">
                <p className="font-semibold text-primary-600">
                  Candidate Exist?
                </p>
                <Form.Item
                  name="isCandidateExist"
                  initialValue={false}
                  labelCol={{ className: 'text-neutral-900' }}
                  label={<span className="text-neutral-900">Yes</span>}
                  className="flex items-center center-form mb-0"
                >
                  <Switch size="small" className="ml-2" />
                </Form.Item>
              </div>
            </div>
          </AppCard>
          {/* Default settings message for existing candidates */}
          <Form.Item
            shouldUpdate={(prev, curr) =>
              prev.isCandidateExist !== curr.isCandidateExist
            }
            noStyle
          >
            {({ getFieldValue }) =>
              getFieldValue('isCandidateExist') && (
                <Alert
                  message="Default settings from the candidate and company will be applied if not selected below"
                  type="info"
                  showIcon
                  className="mb-4"
                />
              )
            }
          </Form.Item>
          <Form.Item
            label="Tax Year"
            name="taxYear"
            rules={[{ required: true, message: 'Please select tax year' }]}
          >
            <AppSelect
              options={taxYearOptions}
              placeholder="Select tax year"
              className="!min-w-0 !w-full !max-w-full"
            />
          </Form.Item>
          <Form.Item
            label="Company Name"
            name="companyId"
            rules={[{ required: true, message: 'Please select company' }]}
          >
            <AppSelectSync
              type="company"
              apiType="searchCompany"
              placeholder="Select company"
              className="w-full max-w-full"
              disabled={!!agencyCompanyRef}
              {...(agencyCompanyRef && {
                defaultValue: {
                  value: agencyCompanyRef.id,
                  label: agencyCompanyRef.companyName,
                },
              })}
              onChangeValue={(
                value: OptionType | OptionType[] | null,
              ) => {
                if (value && !Array.isArray(value)) {
                  handleChangeCompany(value as CompanyPaymentInfo);
                }
              }}
              paramFilter={{
                paymentModel: 'UMBRELLA',
              }}
            />
          </Form.Item>
          <Form.Item
            shouldUpdate={(prev, curr) =>
              prev.isCandidateExist !== curr.isCandidateExist ||
              prev.companyId !== curr.companyId
            }
            noStyle
          >
            {({ getFieldValue }) => {
              const isCandidateExist = getFieldValue('isCandidateExist');
              const company = getFieldValue('companyId');
              return (
                isCandidateExist ? (
                  <Form.Item
                    label="Candidate Name"
                    name="candidateId"
                    rules={[
                      { required: true, message: 'Please select candidate' },
                    ]}
                  >
                    <AppSelectSync
                      type="emp&sub"
                      apiType="searchEmployeeSubcontractV2"
                      placeholder="Select Candidate"
                      className="w-full max-w-full"
                      onChangeValue={(
                        value: OptionType | OptionType[] | null,
                      ) => {
                        if (value && !Array.isArray(value)) {
                          handleChangeCandidateExist(value as CandidateInfo);
                        }
                      }}
                      paramFilter={{
                        paymentModel: 'UMBRELLA',
                        agencyId: agencyPortalId ? agencyPortalId: undefined,
                        companyPaymentIds: company ? [company.value]: undefined
                      }}
                    />
                  </Form.Item>
                ) : (
                  <Form.Item
                    label="Candidate Name"
                    name="candidateName"
                    rules={[
                      { required: true, message: 'Please input candidate name' },
                    ]}
                  >
                    <AppInput
                      placeholder="Enter candidate name"
                      className="w-full max-w-full"
                    />
                  </Form.Item>
                )
              )
            }}
          </Form.Item>
          {!isAgency && (
            <Form.Item
              label="Select Agency"
              name="agencyId"
              // rules={[{ required: true, message: 'Please select agency' }]}
            >
              <AppSelectSync
                type="agency"
                apiType="searchAgency"
                placeholder="Select agency"
                className="w-full max-w-full"
                onChangeValue={(value: OptionType | OptionType[] | null) => {
                  if (value && !Array.isArray(value)) {
                    form.setFieldsValue({ agencyId: value });
                  }
                }}
              />
            </Form.Item>
          )}
          <Form.Item
            label="Email ID"
            name="emailId"
            // rules={[{ required: true, message: 'Please input Email' }]}
          >
            <AppInput
              placeholder="Enter email id"
              className="w-full max-w-full"
              allowCapitalize={false}
            />
          </Form.Item>
          <Form.Item
            label="Pay Frequency"
            name="payFrequency"
            rules={[{ required: true, message: 'Please input pay frequency' }]}
            id="payFrequency"
          >
            <AppSelect
              options={defaultPaymentFrequency}
              placeholder="Select pay frequency"
              className="!min-w-0 !w-full !max-w-full"
            />
          </Form.Item>

          <Form.Item
            label="Pay Rate/Hours"
            name="payRatePerHour"
            rules={[{ required: true, message: 'Please input pay rate/hours' }]}
          >
            <AppCurrencyInput
              prefix={showCurrencyByCountry(payrollCountry)}
              placeholder="Enter pay rate/hours"
              className="w-full max-w-full"
            />
          </Form.Item>
          <Form.Item
            label="No of Hours"
            name="noOfHours"
            rules={[{ required: true, message: 'Please input no of hours' }]}
          >
            <AppRateInput
              placeholder="Enter no of hours"
              className="w-full max-w-full"
            />
          </Form.Item>
          <Form.Item
            label="Margin"
            name="margin"
            rules={[{ required: true, message: 'Please input margin' }]}
          >
            <AppCurrencyInput
              prefix={showCurrencyByCountry(payrollCountry)}
              placeholder="Enter margin"
              className="w-full max-w-full"
            />
          </Form.Item>
          <Form.Item
            label="Tax Code"
            name="taxCode"
            rules={[
              { required: true, message: 'Please input tax code' },
              taxCodeValidationRule(),
            ]}
          >
            <AppInput
              upperCaseAll
              placeholder="Enter tax code"
              className="w-full max-w-full"
            />
          </Form.Item>
          <Form.Item
            label="Age"
            name="age"
            rules={[{ required: true, message: 'Please input age' }]}
          >
            <AppRateInput
              placeholder="Enter age"
              className="w-full max-w-full"
            />
          </Form.Item>
          <Form.Item
            label="Expense"
            name="expenses"
            rules={[{ required: false }]}
          >
            <AppCurrencyInput
              prefix={showCurrencyByCountry(payrollCountry)}
              placeholder="Enter expenses"
              className="w-full max-w-full"
            />
          </Form.Item>
          <Form.Item
            label="Holiday Pay Method"
            name="holidayPay"
            rules={[{ required: false }]}
            id="holidayPay"
          >
            <AppSelect
              options={umbrellaHolidayPaymentMethodOptions}
              placeholder="Select method"
              className="!min-w-0 !w-full !max-w-full"
            />
          </Form.Item>

          <Form.Item
            label="App Levy"
            name="appLevy"
            rules={[{ required: false }]}
          >
            <AppRadioGroup>
              <AppRadioInput value={true}>Yes</AppRadioInput>
              <AppRadioInput value={false}>No</AppRadioInput>
            </AppRadioGroup>
          </Form.Item>
          <Form.Item
            label="Pension"
            name="pension"
            rules={[{ required: false }]}
          >
            <AppRadioGroup>
              <AppRadioInput value={true}>Yes</AppRadioInput>
              <AppRadioInput value={false}>No</AppRadioInput>
            </AppRadioGroup>
          </Form.Item>
          <div className="flex justify-end border-t border-neutral-200 pt-5 ml-[-20px] mr-[-20px] px-5">
            <AppButton
              key="calculate"
              name="Calculate"
              variant="outlined"
              type="primary"
              onClick={() => handlePreviewAction()}
              disabled={loading}
              loading={loading}
            />
          </div>
        </AppFormContainer>
      </div>
    </>
  );
};

export default CreateForm;
